import { useEffect, useState, useCallback } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getPaymentPeriods, createPaymentPeriod, updatePaymentPeriod, deletePaymentPeriod, updateMemberPayment,
  regenerateInvoices, downloadInvoice, getFinancialYears, notifyMemberInvoice, exportPayments,
} from '../services/ApiService';
import type { PaymentPeriod, MemberPayment, FinancialYearSummary } from '../types/models';
import Modal from '../components/Modal';
import { Plus, CreditCard, ChevronDown, ChevronUp, Check, Clock, Trash2, Pencil, RefreshCw, FileText, Send, Download } from 'lucide-react';

export default function PaymentsPage() {
  const { currentOrg, isBoardMember, members } = useOrganization();
  const { user } = useAuth();
  const [periods, setPeriods] = useState<PaymentPeriod[]>([]);
  const [financialYears, setFinancialYears] = useState<FinancialYearSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', description: '', amount: '', dueDate: '', financialYearId: '' });
  const [saving, setSaving] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<PaymentPeriod | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', amount: '', dueDate: '', financialYearId: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchPeriods = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await getPaymentPeriods(currentOrg.id);
      setPeriods(data);
    } catch {
      setPeriods([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  const fetchFinancialYears = useCallback(async () => {
    if (!currentOrg || !isBoardMember) return;
    try {
      const data = await getFinancialYears(currentOrg.id);
      setFinancialYears(data.filter(y => y.status === 'open'));
    } catch {
      setFinancialYears([]);
    }
  }, [currentOrg, isBoardMember]);

  useEffect(() => { fetchPeriods(); fetchFinancialYears(); }, [fetchPeriods, fetchFinancialYears]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    setSaving(true);

    try {
      await createPaymentPeriod(currentOrg.id, {
        title: form.title,
        description: form.description,
        amount: parseFloat(form.amount),
        dueDate: form.dueDate,
        financialYearId: form.financialYearId || null,
      });
      setShowModal(false);
      setForm({ title: '', description: '', amount: '', dueDate: '', financialYearId: '' });
      fetchPeriods();
    } catch {
      alert('Kunde inte skapa betalningsperioden.');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (period: PaymentPeriod) => {
    setEditingPeriod(period);
    setEditForm({
      title: period.title,
      description: period.description,
      amount: String(period.amount),
      dueDate: period.dueDate.slice(0, 10),
      financialYearId: period.financialYearId ?? '',
    });
    setShowEditModal(true);
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !editingPeriod) return;
    setSavingEdit(true);
    try {
      await updatePaymentPeriod(currentOrg.id, editingPeriod.id, {
        title: editForm.title,
        description: editForm.description,
        amount: parseFloat(editForm.amount),
        dueDate: editForm.dueDate,
        financialYearId: editForm.financialYearId || null,
      });
      setShowEditModal(false);
      setEditingPeriod(null);
      fetchPeriods();
    } catch {
      alert('Kunde inte spara ändringarna.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleGenerateInvoices = async (periodId: string) => {
    if (!currentOrg) return;
    setRegeneratingId(periodId);
    try {
      await regenerateInvoices(currentOrg.id, periodId);
      fetchPeriods();
    } catch {
      alert('Kunde inte generera fakturorna.');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleRegenerateInvoices = async (periodId: string) => {
    if (!currentOrg) return;
    setRegeneratingId(periodId);
    try {
      await regenerateInvoices(currentOrg.id, periodId);
      fetchPeriods();
    } catch {
      alert('Kunde inte generera om fakturorna.');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleExport = async () => {
    if (!currentOrg) return;
    try {
      await exportPayments(currentOrg.id);
    } catch {
      alert('Kunde inte exportera betalningarna.');
    }
  };

  const handleDownloadInvoice = async (periodId: string, payment: MemberPayment) => {
    if (!currentOrg) return;
    try {
      await downloadInvoice(currentOrg.id, periodId, payment.id, `faktura-${payment.invoiceNumber}.pdf`);
    } catch {
      alert('Kunde inte hämta fakturan.');
    }
  };

  const handleNotifyMember = async (periodId: string, payment: MemberPayment) => {
    if (!currentOrg) return;
    setNotifyingId(payment.id);
    try {
      await notifyMemberInvoice(currentOrg.id, periodId, payment.id);
      setSentIds(prev => new Set(prev).add(payment.id));
    } catch (err: unknown) {
      alert(err instanceof Error && err.message ? err.message : 'Kunde inte skicka aviseringen.');
    } finally {
      setNotifyingId(null);
    }
  };

  const togglePaid = async (periodId: string, payment: MemberPayment) => {
    if (!currentOrg) return;
    try {
      await updateMemberPayment(currentOrg.id, periodId, payment.id, !payment.isPaid);
      fetchPeriods();
    } catch {
      // ignore
    }
  };

  const handleDeletePeriod = async (id: string) => {
    if (!currentOrg) return;
    if (!window.confirm('Ta bort denna betalningsperiod?')) return;
    try {
      await deletePaymentPeriod(currentOrg.id, id);
      fetchPeriods();
    } catch {
      alert('Kunde inte ta bort perioden.');
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return <div className="p-8 text-center text-gray-500">Laddar...</div>;

  const myPayments = !isBoardMember
    ? periods.map(p => ({
        period: p,
        payment: p.memberPayments?.find(mp => mp.memberId === members.find(m => m.userId === user?.id)?.id),
      })).filter(x => x.payment)
    : null;

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Betalningar</h1>
          <p className="text-gray-500 text-sm mt-1">{isBoardMember ? 'Hantera avgifter och betalningar' : 'Dina avgifter'}</p>
        </div>
        {isBoardMember && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium text-sm"
            >
              <Download className="w-4 h-4" />
              Exportera
            </button>
            <button
              data-tour="payment-add"
              onClick={() => {
                const days = currentOrg?.billingPaymentDays || 30;
                const due = new Date();
                due.setDate(due.getDate() + days);
                setForm({ title: '', description: '', amount: '', dueDate: due.toISOString().split('T')[0], financialYearId: '' });
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Ny betalningsperiod
            </button>
          </div>
        )}
      </div>

      {!isBoardMember && myPayments ? (
        myPayments.length === 0 ? (
          <div className="text-center py-16">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Inga betalningar registrerade</p>
          </div>
        ) : (
          <div className="space-y-3">
            {myPayments.map(({ period, payment }) => (
              <div key={period.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{period.title}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Forfallodag: {formatDate(period.dueDate)}</p>
                    {payment?.invoiceNumber && (
                      <p className="text-xs text-gray-400 mt-0.5">{payment.invoiceNumber}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{payment?.amount?.toLocaleString('sv-SE')} kr</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${payment?.isPaid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      {payment?.isPaid ? <><Check className="w-3 h-3" /> Betald</> : <><Clock className="w-3 h-3" /> Obetald</>}
                    </span>
                  </div>
                </div>
                {payment?.invoiceDownloadUrl ? (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleDownloadInvoice(period.id, payment)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      Visa faktura (PDF)
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      Faktura ej genererad ännu
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        periods.length === 0 ? (
          <div className="text-center py-16">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Inga betalningsperioder skapade</p>
          </div>
        ) : (
          <div className="space-y-3">
            {periods.map(p => {
              const paid = p.memberPayments?.filter(mp => mp.isPaid).length ?? 0;
              const total = p.memberPayments?.length ?? 0;
              const expanded = expandedId === p.id;

              return (
                <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div onClick={() => setExpandedId(expanded ? null : p.id)} className="w-full p-5 flex items-center justify-between text-left cursor-pointer">
                    <div>
                      <h3 className="font-semibold text-gray-900">{p.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span>Forfallodag: {formatDate(p.dueDate)}</span>
                        <span className="text-gray-300">|</span>
                        <span>{p.amount.toLocaleString('sv-SE')} kr</span>
                        <span className="text-gray-300">|</span>
                        <span className={paid === total ? 'text-green-600' : 'text-amber-600'}>{paid}/{total} betalda</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={e => { e.stopPropagation(); openEditModal(p); }} className="p-1.5 rounded-lg hover:bg-teal-50 hover:text-teal-700 transition-colors" title="Redigera">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDeletePeriod(p.id); }} className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 transition-colors" title="Ta bort">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                  {expanded && (
                    <div className="border-t border-gray-100">
                      <div className="divide-y divide-gray-50">
                        {p.memberPayments?.map(mp => (
                          <div key={mp.id} className="px-5 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                                {mp.memberName?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{mp.memberName}</p>
                                <p className="text-xs text-gray-500">{mp.amount.toLocaleString('sv-SE')} kr{mp.invoiceNumber ? ` — ${mp.invoiceNumber}` : ''}</p>
                                {mp.reminderSentAt && !mp.isPaid && (
                                  <p className="text-xs text-amber-700">Påminnelse skickad {new Date(mp.reminderSentAt).toLocaleDateString('sv-SE')}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {mp.invoiceDownloadUrl && (
                                <button
                                  onClick={() => handleDownloadInvoice(p.id, mp)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-50 text-gray-600 hover:bg-gray-100 transition-all"
                                  title="Ladda ner faktura"
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  PDF
                                </button>
                              )}
                              {mp.invoiceDownloadUrl && (
                                <button
                                  onClick={() => handleNotifyMember(p.id, mp)}
                                  disabled={notifyingId === mp.id || sentIds.has(mp.id)}
                                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-100 ${
                                    sentIds.has(mp.id)
                                      ? 'bg-green-50 text-green-700'
                                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50'
                                  }`}
                                  title={sentIds.has(mp.id) ? 'Faktura skickad' : 'Skicka faktura'}
                                >
                                  {sentIds.has(mp.id) ? (
                                    <><Check className="w-3.5 h-3.5" /> Skickat</>
                                  ) : (
                                    <><Send className={`w-3.5 h-3.5 ${notifyingId === mp.id ? 'animate-pulse' : ''}`} /> Skicka</>
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => togglePaid(p.id, mp)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  mp.isPaid ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                                }`}
                              >
                                {mp.isPaid ? <><Check className="w-3.5 h-3.5" /> Betald</> : <><Clock className="w-3.5 h-3.5" /> Markera betald</>}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {!p.memberPayments?.some(mp => mp.invoiceDownloadUrl) && (
                        <div className="px-5 py-3 border-t border-gray-100">
                          <button
                            onClick={() => handleGenerateInvoices(p.id)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                          >
                            <FileText className="w-4 h-4" />
                            Generera fakturor (PDF)
                          </button>
                        </div>
                      )}
                      {p.memberPayments?.some(mp => mp.invoiceDownloadUrl) && (
                        <div className="px-5 py-3 border-t border-gray-100">
                          <button
                            onClick={() => handleRegenerateInvoices(p.id)}
                            disabled={regeneratingId === p.id}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${regeneratingId === p.id ? 'animate-spin' : ''}`} />
                            {regeneratingId === p.id ? 'Genererar om...' : 'Generera om fakturor (PDF)'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Ny betalningsperiod">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" placeholder="t.ex. Årsavgift 2024" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Forfallodag</label>
              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
              {currentOrg?.billingPaymentDays && (
                <p className="text-xs text-gray-500 mt-1">Betalningsvillkor: {currentOrg.billingPaymentDays} dagar (från Inställningar)</p>
              )}
            </div>
          </div>
          {financialYears.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Bokföringsår</label>
              <select
                value={form.financialYearId}
                onChange={e => setForm({ ...form, financialYearId: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white"
              >
                <option value="">Ingen koppling</option>
                {financialYears.map(fy => (
                  <option key={fy.id} value={fy.id}>Räkenskapsår {fy.year}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">Kopplas perioden till ett bokföringsår genereras fakturor automatiskt.</p>
            </div>
          )}
          <p className="text-xs text-gray-500">Betalningar skapas automatiskt för alla nuvarande {members.length} medlemmar. Om en medlem har en andelstal satt, beräknas beloppet proportionellt.</p>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving ? 'Skapar...' : 'Skapa betalningsperiod'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setEditingPeriod(null); }} title="Redigera betalningsperiod">
        <form onSubmit={handleEditSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
            <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <textarea value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={2} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Belopp (SEK)</label>
              <input type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} required min="0" step="0.01" className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Forfallodag</label>
              <input type="date" value={editForm.dueDate} onChange={e => setEditForm({ ...editForm, dueDate: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
            </div>
          </div>
          {financialYears.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Bokföringsår</label>
              <select
                value={editForm.financialYearId}
                onChange={e => setEditForm({ ...editForm, financialYearId: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white"
              >
                <option value="">Ingen koppling</option>
                {financialYears.map(fy => (
                  <option key={fy.id} value={fy.id}>Räkenskapsår {fy.year}</option>
                ))}
              </select>
            </div>
          )}
          <p className="text-xs text-amber-600">OBS: Ändring av belopp uppdaterar inte redan genererade fakturor eller medlemsbetalningar — använd "Generera om fakturor" vid behov.</p>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => { setShowEditModal(false); setEditingPeriod(null); }} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={savingEdit} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {savingEdit ? 'Sparar...' : 'Spara ändringar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
